import React from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { useAuth } from './context/AuthContext'

// Layouts
import PublicLayout from './components/layout/PublicLayout'
import AuthLayout from './components/layout/AuthLayout'
import DashboardLayout from './components/layout/DashboardLayout'
import AdminPortalLayout from './components/layout/AdminPortalLayout'

// Public Pages
import Home from './pages/public/Home'
import About from './pages/public/About'
import Services from './pages/public/Services'
import Contact from './pages/public/Contact'

// User Auth Pages
import Login from './pages/auth/Login'
import Register from './pages/auth/Register'

// User Pages
import Dashboard from './pages/user/Dashboard'
import Transfer from './pages/user/Transfer'
import Settings from './pages/user/Settings'
import ProfileSettings from './pages/user/ProfileSettings'
import TransactionList from './pages/user/TransactionList'

// Admin Portal Pages
import AdminLogin from './pages/admin-portal/AdminLogin'
import AdminDashboard from './pages/admin-portal/AdminDashboard'
import PendingApprovals from './pages/admin-portal/PendingApprovals'
import UserManagement from './pages/admin-portal/UserManagement'
import AllTransactions from './pages/admin-portal/AllTransactions'
import ActivityLogs from './pages/admin-portal/ActivityLogs'
import KYCReview from './pages/admin-portal/KYCReview'
import Analytics from './pages/admin-portal/Analytics'
import SystemSettings from './pages/admin-portal/SystemSettings'
import AdminUserChat from './components/admin/UserChat'

// Loading component
const LoadingScreen = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-dark-900">
    <div className="text-center">
      <div className="spinner w-12 h-12 mx-auto mb-4"></div>
      <p className="text-gray-600 dark:text-gray-400">Loading...</p>
    </div>
  </div>
)

// Protected route - USER ONLY
const UserRoute = ({ children }) => {
  const { isAuthenticated, isAdmin, loading } = useAuth()

  if (loading) return <LoadingScreen />
  if (!isAuthenticated) return <Navigate to="/login" replace />
  if (isAdmin) return <Navigate to="/admin-portal" replace />

  return children
}

// Protected route - ADMIN ONLY
const AdminRoute = ({ children }) => {
  const { isAuthenticated, isAdmin, loading } = useAuth()

  if (loading) return <LoadingScreen />
  if (!isAuthenticated) return <Navigate to="/admin-portal/login" replace />
  if (!isAdmin) return <Navigate to="/dashboard" replace />

  return children
}

// Public route wrapper (redirect if authenticated)
const PublicOnlyRoute = ({ children }) => {
  const { isAuthenticated, isAdmin, loading } = useAuth()

  if (loading) return <LoadingScreen />
  if (isAuthenticated && isAdmin) return <Navigate to="/admin-portal" replace />
  if (isAuthenticated) return <Navigate to="/dashboard" replace />

  return children
}

function App() {
  return (
    <Routes>
      {/* Public Website Routes */}
      <Route element={<PublicLayout />}>
        <Route path="/" element={<Home />} />
        <Route path="/about" element={<About />} />
        <Route path="/services" element={<Services />} />
        <Route path="/contact" element={<Contact />} />
      </Route>

      {/* User Authentication Routes */}
      <Route element={<AuthLayout />}>
        <Route
          path="/login"
          element={
            <PublicOnlyRoute>
              <Login />
            </PublicOnlyRoute>
          }
        />
        <Route
          path="/register"
          element={
            <PublicOnlyRoute>
              <Register />
            </PublicOnlyRoute>
          }
        />
      </Route>

      {/* Admin Portal - Separate Login */}
      <Route element={<AuthLayout adminPortal />}>
        <Route
          path="/admin-portal/login"
          element={
            <PublicOnlyRoute>
              <AdminLogin />
            </PublicOnlyRoute>
          }
        />
      </Route>

      {/* User Dashboard Routes */}
      <Route
        element={
          <UserRoute>
            <DashboardLayout />
          </UserRoute>
        }
      >
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/transfer" element={<Transfer />} />
        <Route path="/profile" element={<ProfileSettings />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/transactions" element={<TransactionList />} />
      </Route>

      {/* Admin Portal Routes */}
      <Route element={<AdminRoute><AdminPortalLayout /></AdminRoute>}>
        <Route path="/admin-portal" element={<AdminDashboard />} />
        <Route path="/admin-portal/pending-approvals" element={<PendingApprovals />} />
        <Route path="/admin-portal/users" element={<UserManagement />} />
        <Route path="/admin-portal/transactions" element={<AllTransactions />} />
        <Route path="/admin/kyc-review" element={<KYCReview />} />
        <Route path="/admin-portal/activity" element={<ActivityLogs />} />
        <Route path="/admin-portal/analytics" element={<Analytics />} />
        <Route path="/admin-portal/settings" element={<SystemSettings />} />
        <Route path="/admin-portal/chat" element={<AdminUserChat />} />
      </Route>

      {/* Catch all - redirect to home */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}

export default App